/*!
 * 自營 Bot 數據（v2.28.3）
 *
 * 自營 Bot = 我方自己研發的開桌機器人俱樂部。聯盟全額承擔它的 rake + 輸贏（含 JP）。
 *
 * 這頁刻意做得比 EcoBot 頁輕：EcoBot 頁複雜是因為要跟對方對帳；
 * 自營 Bot 沒有對帳對象，一週一 row + 幾張圖就夠。
 *
 * 損益口徑：
 *   聯盟損益 = rake + 水後輸贏(含JP) + 保險(莊家收) = 桌面表現(扣JP) + JP淨
 *   拆出 JP 的用意：p_l 本身已含 JP，不拆就看不出「是不是只是輸給 JP」。
 *   加保險(莊家收)的理由：p_l 本身也已含保險（Bot 付的保費已扣在裡面），而收保費的莊家是我方。
 *   保險欄顯示莊家角度（insurance_house，正＝我方收到），緊接在水後輸贏後面，整列由左往右加得起來。
 *   這頁每一週都照實算（含舊週）；營收頁要到 20260921 週起才把保險另列一行，見 self_bot_service 檔頭。
 *   數字全由後端 self_bot_service.union_net() 算好，這裡只顯示。
 */
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Card, CardBody, CardHeader, CardTitle, Row, Col, Table, Alert, TabContent, TabPane, Input } from "reactstrap";
import TinyPageHeader from "components/tiny/TinyPageHeader";
import TinyPageTabs from "components/tiny/TinyPageTabs";
import TinyLoading, { TinyLoadingOverlay } from "components/tiny/TinyLoading";
import axios from "axios";
import { Line, Bar } from "react-chartjs-2";
import {
  Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement,
  BarElement, Title, Tooltip, Legend, Filler,
} from "chart.js";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Legend, Filler);

const API_BASE = "/api/tiny";

// 與 TinyBiweekly / TinyClubConfig 同一套 design token
const S = {
  cardBg: "#1e2530",
  headerBg: "#252d3a",
  border: "rgba(255,255,255,0.1)",
  borderLight: "rgba(255,255,255,0.05)",
  textPrimary: "#fff",
  textSecondary: "rgba(255,255,255,0.8)",
  textMuted: "rgba(255,255,255,0.5)",
  selfBot: "#00d6b4",
  selfBotGrad: "linear-gradient(135deg,#00d6b4,#0d9488)",
  jp: "#e14eca",
  table: "#36a2eb",
  pos: "#2dce89",
  neg: "#f5365c",
};

const thStyle = {
  background: S.headerBg, color: S.textPrimary, fontWeight: 600,
  borderColor: S.border, fontSize: "12px", whiteSpace: "nowrap", padding: "10px 12px",
};
const tdStyle = {
  color: S.textSecondary, borderColor: S.borderLight,
  fontSize: "13px", padding: "9px 12px", whiteSpace: "nowrap",
};

const fmt = (n) =>
  n === null || n === undefined ? "-" : Math.round(n).toLocaleString();
const fmtK = (n) => {
  if (n === null || n === undefined) return "-";
  const a = Math.abs(n);
  if (a >= 1000000) return (n / 1000000).toFixed(2) + "M";
  if (a >= 1000) return (n / 1000).toFixed(1) + "K";
  return Math.round(n).toLocaleString();
};
const signColor = (n) => (Number(n) > 0 ? S.pos : Number(n) < 0 ? S.neg : S.textMuted);

const shortWeek = (w) => (w || "").replace(/^(\d{4})(\d{2})(\d{2})-(\d{4})(\d{2})(\d{2})$/, "$2/$3–$5/$6");

/** KPI 卡：正負值用顏色，副標放拆解 */
const Kpi = ({ title, value, sub, color }) => (
  <div style={{
    background: S.cardBg, border: `1px solid ${S.border}`, borderRadius: 8,
    padding: "14px 18px", height: "100%",
  }}>
    <div style={{ color: S.textMuted, fontSize: 12, marginBottom: 6 }}>{title}</div>
    <div style={{ color: color || S.textPrimary, fontWeight: 700, fontSize: "1.6em", lineHeight: 1.1 }}>
      {value}
    </div>
    {sub && <div style={{ color: S.textMuted, fontSize: 11, marginTop: 6 }}>{sub}</div>}
  </div>
);

function TinySelfBot() {
  const [activeTab, setActiveTab] = useState("trend");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [trend, setTrend] = useState(null);
  const [pool, setPool] = useState(null);
  const [detailWeek, setDetailWeek] = useState("");
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const loadTrend = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [t, p] = await Promise.all([
        axios.get(`${API_BASE}/self-bot/trend`, { params: { limit: 26 } }),
        axios.get(`${API_BASE}/self-bot/pool`).catch(() => ({ data: { code: -1 } })),
      ]);
      if (t.data.code !== 1) throw new Error(t.data.msg || "載入失敗");
      setTrend(t.data.data);
      if (p.data.code === 1) setPool(p.data.data);
      const weeks = t.data.data.weeks || [];
      if (weeks.length) setDetailWeek((w) => w || weeks[0]);
    } catch (e) {
      setError(e.response?.data?.msg || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadTrend(); }, [loadTrend]);

  useEffect(() => {
    if (!detailWeek) return;
    let cancelled = false;
    setDetailLoading(true);
    axios.get(`${API_BASE}/self-bot/detail/${detailWeek}`)
      .then((r) => { if (!cancelled && r.data.code === 1) setDetail(r.data.data); })
      .catch(() => { if (!cancelled) setDetail(null); })
      .finally(() => { if (!cancelled) setDetailLoading(false); });
    return () => { cancelled = true; };
  }, [detailWeek]);

  // 圖表資料：由舊到新
  const chrono = useMemo(() => (trend?.rows ? [...trend.rows].reverse() : []), [trend]);

  const chartOpts = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { labels: { color: "#fff", font: { size: 11 }, padding: 12 } },
      tooltip: {
        backgroundColor: "rgba(0,0,0,0.85)", padding: 10,
        callbacks: { label: (c) => `${c.dataset.label}: ${fmt(c.parsed.y)}` },
      },
    },
    scales: {
      x: { ticks: { color: "#9A9A9A", font: { size: 10 } }, grid: { display: false } },
      y: {
        ticks: { color: "#9A9A9A", font: { size: 10 }, callback: (v) => fmtK(v) },
        grid: { color: "rgba(255,255,255,0.08)" },
      },
    },
  }), []);

  const netChart = useMemo(() => ({
    labels: chrono.map((r) => shortWeek(r.week)),
    datasets: [
      {
        type: "bar",
        label: "當週聯盟損益",
        data: chrono.map((r) => r.net),
        backgroundColor: chrono.map((r) => (r.net >= 0 ? "rgba(45,206,137,0.65)" : "rgba(245,54,92,0.65)")),
        borderColor: chrono.map((r) => (r.net >= 0 ? S.pos : S.neg)),
        borderWidth: 1,
        order: 2,
      },
      {
        type: "line",
        label: "累計損益",
        data: chrono.map((r) => r.cum_net),
        borderColor: S.selfBot,
        backgroundColor: "rgba(0,214,180,0.12)",
        borderWidth: 2,
        tension: 0.3,
        fill: true,
        order: 1,
      },
    ],
  }), [chrono]);

  // 損益拆解：桌面表現 vs JP 淨，兩根堆疊起來就是聯盟損益
  const splitChart = useMemo(() => ({
    labels: chrono.map((r) => shortWeek(r.week)),
    datasets: [
      {
        label: "桌面表現（扣JP）",
        data: chrono.map((r) => r.pre_rake_pl_exclude_jp),
        backgroundColor: "rgba(54,162,235,0.7)",
        stack: "s",
      },
      {
        label: "JP 淨",
        data: chrono.map((r) => r.jp_net),
        backgroundColor: "rgba(225,78,202,0.7)",
        stack: "s",
      },
    ],
  }), [chrono]);

  const splitOpts = useMemo(() => ({
    ...chartOpts,
    scales: {
      x: { ...chartOpts.scales.x, stacked: true },
      y: { ...chartOpts.scales.y, stacked: true },
    },
  }), [chartOpts]);

  const latest = trend?.rows?.[0];
  const totals = trend?.totals;

  if (loading) {
    return (
      <div className="content">
        <TinyLoading text="載入中" variant="page" />
      </div>
    );
  }

  if (error) {
    return <div className="content"><Alert color="danger">{error}</Alert></div>;
  }

  if (!trend?.rows?.length) {
    return (
      <div className="content">
        <Alert color="info">
          目前沒有任何自營 Bot 資料。<br />
          自營 Bot 的歸屬走「該週 pipeline 跑時的 snapshot」——
          請先到「俱樂部管理」把俱樂部勾選為<b>自營 Bot 俱樂部</b>，再跑一次該週 pipeline。
        </Alert>
      </div>
    );
  }

  return (
    <div className="content">
      {/* ⚠️ tabs **不放進 header** —— 這頁的 KPI 卡是頁面層級的（不隨 tab 變），
          tabs 只管底下的表格，所以要留在 KPI 之後。用 standalone 的
          TinyPageTabs（見下方）。 */}
      <TinyPageHeader
        title="自營 Bot 數據"
        badges={[{ text: "自營BOT", tone: "success" }]}
        description="聯盟全額承擔 rake + 輸贏（含 JP）· 賺賠全是我方的"
        meta={(trend.clubs || []).map((c) => `${c.club_id} ${c.club_name}`).join("、")}
      />

      {/* ===== KPI ===== */}
      <Row className="mb-3">
        <Col lg="3" md="6" className="mb-3">
          <Kpi title={`本週聯盟損益（${shortWeek(latest.week)}）`}
            value={fmt(latest.net)} color={signColor(latest.net)}
            sub={`桌面 ${fmt(latest.pre_rake_pl_exclude_jp)} ＋ JP ${fmt(latest.jp_net)}`} />
        </Col>
        <Col lg="3" md="6" className="mb-3">
          <Kpi title="累計聯盟損益" value={fmt(latest.cum_net)} color={signColor(latest.cum_net)}
            sub={`${trend.rows.length} 週累計 · 單週波動大，看這格`} />
        </Col>
        <Col lg="3" md="6" className="mb-3">
          <Kpi title="累計 JP 淨" value={fmt(latest.cum_jp_net)} color={signColor(latest.cum_jp_net)}
            sub={`fee ${fmt(totals.jp_fee)} / 中獎 ${fmt(totals.jp_payout)}`} />
        </Col>
        <Col lg="3" md="6" className="mb-3">
          <Kpi title="累計桌面表現（扣JP）" value={fmt(latest.cum_pre_rake_pl_exclude_jp)}
            color={signColor(latest.cum_pre_rake_pl_exclude_jp)}
            sub={`${fmt(totals.hands)} 手 · ${totals.player_count ? Math.round(totals.player_count) : 0} 筆帳號週`} />
        </Col>
      </Row>

      {/* ===== 獎池提示 ===== */}
      {pool?.self_bot && pool.self_bot.net_out > 0 && (
        <Row>
          <Col md="12">
            <div style={{
              display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap",
              padding: "12px 18px", marginBottom: 16, borderRadius: 8,
              background: "rgba(245,54,92,0.10)", border: "1px solid rgba(245,54,92,0.4)",
            }}>
              <i className="fa fa-exclamation-triangle" style={{ color: S.neg }} />
              <span style={{ color: S.textPrimary, fontSize: 13 }}>
                雙週獎池（{pool.biweek_id}）：自營Bot 貢獻 <b>{fmt(pool.self_bot.water)}</b> 水、
                灌入獎池 <b>{fmt(pool.self_bot.pool_in)}</b>、回收 <b>{fmt(pool.self_bot.recovered)}</b> →
                <b style={{ color: S.neg, marginLeft: 4 }}>淨流出 {fmt(pool.self_bot.net_out)}</b> 送給別家。
                請到雙週獎池頁做技術性調整扣除。
              </span>
            </div>
          </Col>
        </Row>
      )}

      <TinyPageTabs
        items={[
          { key: "trend", label: "週趨勢", icon: "fa fa-line-chart" },
          { key: "detail", label: "機器人明細", icon: "fa fa-android" },
          { key: "pool", label: "獎池", icon: "fa fa-trophy" },
        ]}
        active={activeTab}
        onChange={setActiveTab}
      />

      <TabContent activeTab={activeTab}>
        {/* ---------- 週趨勢 ---------- */}
        <TabPane tabId="trend">
          <Row>
            <Col md="12">
              <Card style={{ background: S.cardBg, border: `1px solid ${S.border}` }}>
                <CardHeader>
                  <CardTitle tag="h5" style={{ marginBottom: 0, color: S.textPrimary }}>
                    <i className="fa fa-android mr-2" style={{ color: S.selfBot }} />每週損益
                  </CardTitle>
                  <p className="card-category" style={{ marginBottom: 0 }}>
                    聯盟損益 ＝ Rake ＋ 水後輸贏(含JP) ＋ 保險(莊家收) ＝ 桌面表現(扣JP) ＋ JP淨
                  </p>
                </CardHeader>
                <CardBody style={{ overflowX: "auto" }}>
                  <Table style={{ marginBottom: 0 }}>
                    <thead>
                      <tr>
                        <th style={thStyle}>週期</th>
                        <th style={{ ...thStyle, textAlign: "right" }}>帳號</th>
                        <th style={{ ...thStyle, textAlign: "right" }}>手數</th>
                        <th style={{ ...thStyle, textAlign: "right" }}>Rake</th>
                        <th style={{ ...thStyle, textAlign: "right" }}>水後輸贏</th>
                        <th style={{ ...thStyle, textAlign: "right" }}>保險(莊家收)*</th>
                        <th style={{ ...thStyle, textAlign: "right" }}>JP Fee</th>
                        <th style={{ ...thStyle, textAlign: "right" }}>JP Payout</th>
                        <th style={{ ...thStyle, textAlign: "right", color: S.jp }}>JP 淨</th>
                        <th style={{ ...thStyle, textAlign: "right", color: S.table }}>桌面(扣JP)</th>
                        <th style={{ ...thStyle, textAlign: "right", color: S.selfBot }}>聯盟損益</th>
                        <th style={{ ...thStyle, textAlign: "right" }}>累計</th>
                      </tr>
                    </thead>
                    <tbody>
                      {trend.rows.map((r) => (
                        <tr key={r.week}>
                          <td style={{ ...tdStyle, fontFamily: "monospace" }}>{r.week}</td>
                          <td style={{ ...tdStyle, textAlign: "right" }}>{r.player_count}</td>
                          <td style={{ ...tdStyle, textAlign: "right" }}>{fmt(r.hands)}</td>
                          <td style={{ ...tdStyle, textAlign: "right", fontFamily: "monospace" }}>{fmt(r.rake)}</td>
                          <td style={{ ...tdStyle, textAlign: "right", fontFamily: "monospace", color: signColor(r.player_pl) }}>{fmt(r.player_pl)}</td>
                          <td style={{ ...tdStyle, textAlign: "right", fontFamily: "monospace", color: signColor(r.insurance_house) }}>{fmt(r.insurance_house)}</td>
                          <td style={{ ...tdStyle, textAlign: "right", fontFamily: "monospace" }}>{fmt(r.jp_fee)}</td>
                          <td style={{ ...tdStyle, textAlign: "right", fontFamily: "monospace" }}>{fmt(r.jp_payout)}</td>
                          <td style={{ ...tdStyle, textAlign: "right", fontFamily: "monospace", color: signColor(r.jp_net) }}>{fmt(r.jp_net)}</td>
                          <td style={{ ...tdStyle, textAlign: "right", fontFamily: "monospace", color: signColor(r.pre_rake_pl_exclude_jp) }}>{fmt(r.pre_rake_pl_exclude_jp)}</td>
                          <td style={{ ...tdStyle, textAlign: "right", fontFamily: "monospace", fontWeight: 700, color: signColor(r.net) }}>{fmt(r.net)}</td>
                          <td style={{ ...tdStyle, textAlign: "right", fontFamily: "monospace", color: signColor(r.cum_net) }}>{fmt(r.cum_net)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                  <div style={{ color: S.textMuted, fontSize: 11, marginTop: 10, lineHeight: 1.7 }}>
                    * 保險(莊家收)：Bot 付給莊家（我方）的保費淨額，正＝我方收到。水後輸贏已經扣過這筆，
                    所以聯盟損益要把它<b>加回來</b>（＝ Rake ＋ 水後輸贏 ＋ 保險(莊家收)）。
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>

          <Row>
            <Col lg="6" className="mb-3">
              <Card style={{ background: S.cardBg, border: `1px solid ${S.border}` }}>
                <CardHeader><CardTitle tag="h6" style={{ marginBottom: 0, color: S.textPrimary }}>損益趨勢（柱＝當週、線＝累計）</CardTitle></CardHeader>
                <CardBody><div style={{ height: 260 }}><Bar data={netChart} options={chartOpts} /></div></CardBody>
              </Card>
            </Col>
            <Col lg="6" className="mb-3">
              <Card style={{ background: S.cardBg, border: `1px solid ${S.border}` }}>
                <CardHeader>
                  <CardTitle tag="h6" style={{ marginBottom: 0, color: S.textPrimary }}>損益拆解：桌面 vs JP</CardTitle>
                  <p className="card-category" style={{ marginBottom: 0, fontSize: 11 }}>
                    兩根堆起來 = 當週聯盟損益。看得出是輸在桌上還是輸給 JP。
                  </p>
                </CardHeader>
                <CardBody><div style={{ height: 260 }}><Bar data={splitChart} options={splitOpts} /></div></CardBody>
              </Card>
            </Col>
          </Row>
        </TabPane>

        {/* ---------- 機器人明細 ---------- */}
        <TabPane tabId="detail">
          <Row>
            <Col md="12">
              <Card style={{ background: S.cardBg, border: `1px solid ${S.border}` }}>
                <CardHeader>
                  <Row className="align-items-center">
                    <Col md="8">
                      <CardTitle tag="h5" style={{ marginBottom: 0, color: S.textPrimary }}>
                        <i className="fa fa-list mr-2" style={{ color: S.selfBot }} />機器人明細
                      </CardTitle>
                      <p className="card-category" style={{ marginBottom: 0 }}>虧最多的排前面</p>
                    </Col>
                    <Col md="4">
                      <Input type="select" value={detailWeek} onChange={(e) => setDetailWeek(e.target.value)}
                        style={{ background: "#1a1f2b", color: "#fff", border: `1px solid ${S.border}` }}>
                        {(trend.weeks || []).map((w) => <option key={w} value={w}>{w}</option>)}
                      </Input>
                    </Col>
                  </Row>
                </CardHeader>
                <CardBody style={{ overflowX: "auto" }}>
                  {detailLoading && !detail?.bots?.length ? (
                    <TinyLoading text="載入週期資料" variant="block" />
                  ) : !detail?.bots?.length ? (
                    <Alert color="info" className="mb-0">這週沒有資料</Alert>
                  ) : (
                    <TinyLoadingOverlay active={detailLoading} text="載入週期資料">
                    <Table style={{ marginBottom: 0 }}>
                      <thead>
                        <tr>
                          <th style={thStyle}>帳號</th>
                          <th style={thStyle}>暱稱</th>
                          <th style={{ ...thStyle, textAlign: "right" }}>手數</th>
                          <th style={{ ...thStyle, textAlign: "right" }}>Rake</th>
                          <th style={{ ...thStyle, textAlign: "right" }}>水後輸贏</th>
                          <th style={{ ...thStyle, textAlign: "right" }}>保險(莊家收)*</th>
                          <th style={{ ...thStyle, textAlign: "right" }}>JP Fee</th>
                          <th style={{ ...thStyle, textAlign: "right" }}>JP Payout</th>
                          <th style={{ ...thStyle, textAlign: "right", color: S.table }}>桌面(扣JP)</th>
                          <th style={{ ...thStyle, textAlign: "right", color: S.selfBot }}>損益</th>
                        </tr>
                      </thead>
                      <tbody>
                        {detail.bots.map((b) => (
                          <tr key={b.player_id}>
                            <td style={{ ...tdStyle, fontFamily: "monospace" }}>{b.player_id}</td>
                            <td style={tdStyle}>{b.nickname}</td>
                            <td style={{ ...tdStyle, textAlign: "right" }}>{fmt(b.hands)}</td>
                            <td style={{ ...tdStyle, textAlign: "right", fontFamily: "monospace" }}>{fmt(b.rake)}</td>
                            <td style={{ ...tdStyle, textAlign: "right", fontFamily: "monospace", color: signColor(b.player_pl) }}>{fmt(b.player_pl)}</td>
                            <td style={{ ...tdStyle, textAlign: "right", fontFamily: "monospace", color: signColor(b.insurance_house) }}>{fmt(b.insurance_house)}</td>
                            <td style={{ ...tdStyle, textAlign: "right", fontFamily: "monospace" }}>{fmt(b.jp_fee)}</td>
                            <td style={{ ...tdStyle, textAlign: "right", fontFamily: "monospace" }}>{fmt(b.jp_payout)}</td>
                            <td style={{ ...tdStyle, textAlign: "right", fontFamily: "monospace", color: signColor(b.pre_rake_pl_exclude_jp) }}>{fmt(b.pre_rake_pl_exclude_jp)}</td>
                            <td style={{ ...tdStyle, textAlign: "right", fontFamily: "monospace", fontWeight: 700, color: signColor(b.net) }}>{fmt(b.net)}</td>
                          </tr>
                        ))}
                        {detail.totals && (
                          <tr style={{ borderTop: `2px solid ${S.border}` }}>
                            <td style={{ ...tdStyle, fontWeight: 700, color: S.textPrimary }} colSpan={2}>合計</td>
                            <td style={{ ...tdStyle, textAlign: "right", fontWeight: 700 }}>{fmt(detail.totals.hands)}</td>
                            <td style={{ ...tdStyle, textAlign: "right", fontFamily: "monospace", fontWeight: 700 }}>{fmt(detail.totals.rake)}</td>
                            <td style={{ ...tdStyle, textAlign: "right", fontFamily: "monospace", fontWeight: 700, color: signColor(detail.totals.player_pl) }}>{fmt(detail.totals.player_pl)}</td>
                            <td style={{ ...tdStyle, textAlign: "right", fontFamily: "monospace", fontWeight: 700, color: signColor(detail.totals.insurance_house) }}>{fmt(detail.totals.insurance_house)}</td>
                            <td style={{ ...tdStyle, textAlign: "right", fontFamily: "monospace", fontWeight: 700 }}>{fmt(detail.totals.jp_fee)}</td>
                            <td style={{ ...tdStyle, textAlign: "right", fontFamily: "monospace", fontWeight: 700 }}>{fmt(detail.totals.jp_payout)}</td>
                            <td style={{ ...tdStyle, textAlign: "right", fontFamily: "monospace", fontWeight: 700, color: signColor(detail.totals.pre_rake_pl_exclude_jp) }}>{fmt(detail.totals.pre_rake_pl_exclude_jp)}</td>
                            <td style={{ ...tdStyle, textAlign: "right", fontFamily: "monospace", fontWeight: 700, color: signColor(detail.totals.net) }}>{fmt(detail.totals.net)}</td>
                          </tr>
                        )}
                      </tbody>
                    </Table>
                    </TinyLoadingOverlay>
                  )}
                  <div style={{ color: S.textMuted, fontSize: 11, marginTop: 10, lineHeight: 1.7 }}>
                    * 保險(莊家收)：Bot 付給莊家（我方）的保費淨額，正＝我方收到。水後輸贏已經扣過這筆，
                    所以聯盟損益要把它<b>加回來</b>（＝ Rake ＋ 水後輸贏 ＋ 保險(莊家收)）。
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </TabPane>

        {/* ---------- 獎池 ---------- */}
        <TabPane tabId="pool">
          <Row>
            <Col md="12">
              <Card style={{ background: S.cardBg, border: `1px solid ${S.border}` }}>
                <CardHeader>
                  <CardTitle tag="h5" style={{ marginBottom: 0, color: S.textPrimary }}>
                    <i className="fa fa-trophy mr-2" style={{ color: S.selfBot }} />雙週獎池貢獻
                  </CardTitle>
                  <p className="card-category" style={{ marginBottom: 0 }}>
                    {pool ? `${pool.biweek_id}　基數水量 ${fmt(pool.league_water)}${pool.exclude_aof ? "（排除AOF）" : "（含AOF）"}` : "—"}
                  </p>
                </CardHeader>
                <CardBody style={{ overflowX: "auto" }}>
                  {!pool?.rows?.length ? (
                    <Alert color="info" className="mb-0">還沒有雙週獎池資料</Alert>
                  ) : (
                    <>
                      <Table style={{ marginBottom: 0 }}>
                        <thead>
                          <tr>
                            <th style={thStyle}>分類</th>
                            <th style={{ ...thStyle, textAlign: "right" }}>貢獻水量</th>
                            <th style={{ ...thStyle, textAlign: "right" }}>佔比</th>
                            <th style={{ ...thStyle, textAlign: "right" }}>灌入獎池</th>
                            <th style={{ ...thStyle, textAlign: "right" }}>我方回收</th>
                            <th style={{ ...thStyle, textAlign: "right" }}>淨流出</th>
                          </tr>
                        </thead>
                        <tbody>
                          {pool.rows.map((r) => {
                            const mine = r.label === "自營Bot";
                            return (
                              <tr key={r.label} style={mine ? { background: "rgba(0,214,180,0.10)" } : undefined}>
                                <td style={{ ...tdStyle, fontWeight: mine ? 700 : 400, color: mine ? S.selfBot : S.textSecondary }}>{r.label}</td>
                                <td style={{ ...tdStyle, textAlign: "right", fontFamily: "monospace" }}>{fmt(r.water)}</td>
                                <td style={{ ...tdStyle, textAlign: "right", color: S.textMuted }}>{r.water_pct?.toFixed(1)}%</td>
                                <td style={{ ...tdStyle, textAlign: "right", fontFamily: "monospace" }}>{fmt(r.pool_in)}</td>
                                <td style={{ ...tdStyle, textAlign: "right", fontFamily: "monospace", color: S.pos }}>{fmt(r.recovered)}</td>
                                <td style={{ ...tdStyle, textAlign: "right", fontFamily: "monospace", fontWeight: 700, color: signColor(-r.net_out) }}>{fmt(r.net_out)}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </Table>
                      <div style={{ color: S.textMuted, fontSize: 11, marginTop: 10, lineHeight: 1.7 }}>
                        「淨流出」= 灌入獎池 − 我方回收。自營 Bot 若沒過獎池門檻
                        （輸退：RG水 ≥ 60,000／雙週；活躍：active_score ≥ 50），
                        它貢獻的 13% 會整筆分給別家 —— 請到<b>雙週獎池頁</b>做技術性調整扣除。
                      </div>
                    </>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </TabPane>
      </TabContent>
    </div>
  );
}

export default TinySelfBot;
